import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpDown, RefreshCw, Search } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { StatChip, StatTile } from '@/components/ui/StatTile';
import { fetchCallsInRange, fetchExtensions } from '@/services/api';
import { formatDateTimeForApi } from '@/utils/helpers';
import {
  classifyCalls,
  extensionCallStats,
  type CallsSummary,
  type ClassifiedCall,
} from '@/lib/reports/callClassification';
import { useStore } from '@/store/useStore';
import type { Extension } from '@/types';

const RANGE_CONFIG = {
  today: {
    label: 'Today',
    compareLabel: 'Yesterday',
    bucket: 'hour' as const,
    bucketCount: 24,
    spanDays: 1,
  },
  week: {
    label: 'Last 7 Days',
    compareLabel: 'Previous 7 Days',
    bucket: 'day' as const,
    bucketCount: 7,
    spanDays: 7,
  },
  month: {
    label: 'Last 30 Days',
    compareLabel: 'Previous 30 Days',
    bucket: 'day' as const,
    bucketCount: 30,
    spanDays: 30,
  },
};

type RangeKey = keyof typeof RANGE_CONFIG;

type ActivitySeries = {
  labels: string[];
  current: number[];
  previous: number[];
  currentTotal: number;
  previousTotal: number;
};

type ExtensionStats = {
  id: string;
  number: string;
  label: string;
  received: number;
  made: number;
  unansweredRings: number;
  trulyMissed: number;
  total: number;
};

type ExtensionSortKey = 'total' | 'received' | 'made' | 'unansweredRings' | 'trulyMissed';

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Calls per bucket, one per call (uid); internal calls and short IVR hang-ups are left out, as on the cards. */
function buildActivitySeries(
  calls: ClassifiedCall[],
  start: Date,
  bucketCount: number,
  bucketMs: number
): { series: number[]; total: number } {
  const series = Array.from({ length: bucketCount }, () => 0);
  let total = 0;

  for (const call of calls) {
    if (call.direction === 'internal' || call.shortSpam) continue;
    total += 1;
    const bucketIndex = Math.floor((call.atMs - start.getTime()) / bucketMs);
    if (bucketIndex >= 0 && bucketIndex < bucketCount) series[bucketIndex] += 1;
  }

  return { series, total };
}

function buildExtensionStats(extensions: Extension[], calls: ClassifiedCall[]): ExtensionStats[] {
  const labels = new Map(
    extensions.map((ext) => [
      String(ext.number),
      ext.display_name ? `Ext ${ext.number} (${ext.display_name})` : `Ext ${ext.number}`,
    ])
  );
  return extensionCallStats(calls, extensions)
    .filter((row) => row.total > 0)
    .map((row) => ({
      id: row.ext_num,
      number: row.ext_num,
      label: labels.get(row.ext_num) ?? `Ext ${row.ext_num}${row.ext_name !== row.ext_num ? ` (${row.ext_name})` : ''}`,
      received: row.received,
      made: row.made,
      unansweredRings: row.unansweredRings,
      trulyMissed: row.trulyMissed,
      total: row.total,
    }))
    .sort(
      (a, b) =>
        b.total - a.total ||
        a.number.localeCompare(b.number, undefined, { numeric: true })
    );
}

function ActivityChart({
  labels,
  current,
  previous,
  currentLabel,
  previousLabel,
  futureFrom = labels.length,
}: {
  labels: string[];
  current: number[];
  previous: number[];
  currentLabel: string;
  previousLabel: string;
  /** Buckets from this index on have not happened yet and are drawn hatched. */
  futureFrom?: number;
}) {
  const max = Math.max(1, ...current, ...previous);
  const pct = (value: number) => (value / max) * 100;
  const pastValues = current.slice(0, futureFrom);
  const peak = pastValues.length ? Math.max(...pastValues) : 0;
  const peakIndex = peak > 0 ? pastValues.indexOf(peak) : -1;
  const labelStep = Math.ceil(labels.length / 8);
  const gap = labels.length > 12 ? 'gap-1 sm:gap-1.5' : 'gap-3 sm:gap-5';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-3.5 rounded-full bg-primary-500" />
          {currentLabel}
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-3.5 h-0.5 rounded-full bg-gray-700 dark:bg-gray-200" />
          {previousLabel}
        </div>
      </div>

      <div
        role="group"
        aria-label={`Calls per ${labels.length === 24 ? 'hour' : 'day'}`}
        className={`flex h-52 items-end ${gap}`}
      >
        {current.map((value, index) => {
          const label = labels[index] ?? '';
          const future = index >= futureFrom;
          const before = previous[index] ?? 0;
          const height = Math.max(pct(value), 4);
          return (
            <div
              key={label || index}
              tabIndex={future ? undefined : 0}
              aria-label={
                future ? undefined : `${label}: ${value} calls, ${previousLabel} ${before}`
              }
              className="group relative flex h-full min-w-0 flex-1 justify-center focus:outline-none"
            >
              {future ? (
                <div className="h-full w-full max-w-[36px] rounded-full bg-stripes text-gray-300 dark:text-gray-600" />
              ) : (
                <>
                  <div
                    className={`absolute bottom-0 w-full max-w-[36px] rounded-full transition-colors group-hover:bg-primary-400 group-focus-visible:bg-primary-400 ${
                      value === 0
                        ? 'bg-primary-100 dark:bg-primary-900/50'
                        : index === peakIndex
                          ? 'bg-primary-700 dark:bg-primary-300'
                          : 'bg-primary-500'
                    }`}
                    style={{ height: `${height}%` }}
                  />
                  {before > 0 && (
                    <div
                      aria-hidden="true"
                      className="absolute h-0.5 w-[calc(100%+6px)] max-w-[44px] rounded-full bg-gray-700 dark:bg-gray-200"
                      style={{ bottom: `calc(${pct(before)}% - 1px)` }}
                    />
                  )}
                  {index === peakIndex && (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute whitespace-nowrap rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 group-hover:opacity-0 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                      style={{ bottom: `calc(${height}% + 6px)` }}
                    >
                      {value}
                    </span>
                  )}
                  <div
                    role="tooltip"
                    className="pointer-events-none absolute z-10 whitespace-nowrap rounded-xl bg-gray-900 px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 dark:bg-white dark:text-gray-900"
                    style={{ bottom: `calc(${Math.max(height, pct(before))}% + 8px)` }}
                  >
                    <p>
                      <span className="text-sm font-semibold">{value}</span> calls · {label}
                    </p>
                    <p className="opacity-70">
                      {before} {previousLabel.toLowerCase()}
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className={`mt-3 flex ${gap}`} aria-hidden="true">
        {labels.map((label, index) => (
          <span
            key={label || index}
            className="flex min-w-0 flex-1 justify-center whitespace-nowrap text-[11px] text-gray-500 dark:text-gray-400"
          >
            {index % labelStep === 0 ? label : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SimplifiedAnalytics() {
  const [timeRange, setTimeRange] = useState<RangeKey>('today');
  const [activity, setActivity] = useState<ActivitySeries | null>(null);
  const [extensionStats, setExtensionStats] = useState<ExtensionStats[]>([]);
  const [summary, setSummary] = useState<CallsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const {
    extensions,
    setExtensions,
    analyticsExtensionSearch,
    setAnalyticsExtensionSearch,
  } = useStore();
  const [extensionSort, setExtensionSort] = useState<ExtensionSortKey>('total');
  const rangeConfig = RANGE_CONFIG[timeRange];

  const labels = useMemo(() => {
    if (rangeConfig.bucket === 'hour') {
      return Array.from(
        { length: rangeConfig.bucketCount },
        (_, index) => `${String(index).padStart(2, '0')}:00`
      );
    }

    const start = startOfDay(addDays(new Date(), -(rangeConfig.spanDays - 1)));
    return Array.from({ length: rangeConfig.bucketCount }, (_, index) => {
      const date = addDays(start, index);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    });
  }, [rangeConfig]);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);

    try {
      const now = new Date();
      const currentStart = startOfDay(addDays(now, -(rangeConfig.spanDays - 1)));
      const currentEnd = now;
      const previousStart = startOfDay(addDays(currentStart, -rangeConfig.spanDays));
      const previousEnd = new Date(currentStart.getTime() - 1);

      const extensionData =
        extensions.length > 0 ? extensions : await fetchExtensions(false);
      if (extensions.length === 0) {
        setExtensions(extensionData);
      }

      const [currentRecords, previousRecords] = await Promise.all([
        fetchCallsInRange(formatDateTimeForApi(currentStart), formatDateTimeForApi(currentEnd)),
        fetchCallsInRange(formatDateTimeForApi(previousStart), formatDateTimeForApi(previousEnd)),
      ]);
      const current = classifyCalls(currentRecords, { extensions: extensionData });
      const previous = classifyCalls(previousRecords, { extensions: extensionData });

      const bucketMs =
        rangeConfig.bucket === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;

      // For hour view, bucket from midnight so all of today is covered.
      const seriesStart = rangeConfig.bucket === 'hour' ? startOfDay(now) : currentStart;
      const previousSeriesStart =
        rangeConfig.bucket === 'hour' ? startOfDay(addDays(now, -1)) : previousStart;

      const currentSeries = buildActivitySeries(
        current.calls,
        seriesStart,
        rangeConfig.bucketCount,
        bucketMs
      );
      const previousSeries = buildActivitySeries(
        previous.calls,
        previousSeriesStart,
        rangeConfig.bucketCount,
        bucketMs
      );

      setActivity({
        labels,
        current: currentSeries.series,
        previous: previousSeries.series,
        currentTotal: currentSeries.total,
        previousTotal: previousSeries.total,
      });
      setSummary(current.summary);
      setExtensionStats(buildExtensionStats(extensionData, current.calls));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [extensions, labels, rangeConfig, setExtensions]);

  useEffect(() => {
    void loadAnalytics();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps -- reload on range change only

  const visibleExtensionStats = useMemo(() => {
    const search = analyticsExtensionSearch.trim().toLowerCase();
    return extensionStats
      .filter(
        (stat) =>
          !search ||
          stat.number.toLowerCase().includes(search) ||
          stat.label.toLowerCase().includes(search)
      )
      .sort(
        (a, b) =>
          b[extensionSort] - a[extensionSort] ||
          b.total - a.total ||
          a.number.localeCompare(b.number, undefined, { numeric: true })
      );
  }, [analyticsExtensionSearch, extensionSort, extensionStats]);

  if (loading && !activity) {
    return (
      <Card>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
          Call analytics
        </h2>
        <Loader text="Loading analytics..." />
      </Card>
    );
  }

  // Scale bars by call total per extension (received + made); unanswered rings are a subset of received.
  const maxExtensionTotal = Math.max(1, ...extensionStats.map((stat) => stat.total));
  const delta = activity ? activity.currentTotal - activity.previousTotal : 0;
  const deltaPct =
    activity && activity.previousTotal > 0
      ? Math.round((delta / activity.previousTotal) * 100)
      : null;

  const futureFrom =
    rangeConfig.bucket === 'hour' ? new Date().getHours() + 1 : undefined;

  return (
    <Card>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Call analytics
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Call activity trends and per-extension breakdowns
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(RANGE_CONFIG) as RangeKey[]).map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => setTimeRange(range)}
              className={`px-4 py-1.5 text-sm rounded-full font-medium transition-colors ${
                timeRange === range
                  ? 'bg-primary-600 text-white'
                  : 'bg-panel text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {RANGE_CONFIG[range].label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void loadAnalytics()}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-900 dark:hover:border-gray-300"
            aria-label="Refresh analytics"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : undefined} />
          </button>
        </div>
      </div>

      <div
        className={`space-y-8 transition-opacity ${loading ? 'pointer-events-none opacity-50' : ''}`}
        aria-busy={loading}
      >
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Call activity
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {rangeConfig.label} compared to {rangeConfig.compareLabel.toLowerCase()}
              </p>
            </div>

            {activity && (
              <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-6">
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-1 lg:content-start">
                  <StatTile
                    size="md"
                    surface="inset"
                    label={rangeConfig.label}
                    value={activity.currentTotal}
                    caption={
                      <>
                        <StatChip>
                          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}
                          {deltaPct !== null ? ` · ${Math.abs(deltaPct)}%` : ''}
                        </StatChip>
                        vs {rangeConfig.compareLabel.toLowerCase()}
                      </>
                    }
                  />
                  <StatTile
                    size="md"
                    surface="inset"
                    label={rangeConfig.compareLabel}
                    value={activity.previousTotal}
                    caption="Calls (one per call, not per leg)"
                  />
                </div>
                <ActivityChart
                  labels={activity.labels}
                  current={activity.current}
                  previous={activity.previous}
                  currentLabel={rangeConfig.label}
                  previousLabel={rangeConfig.compareLabel}
                  futureFrom={futureFrom}
                />
              </div>
            )}

          </div>

          <div
            id="extension-call-activity"
            className="border-t border-gray-200 dark:border-gray-700 pt-6 scroll-mt-6"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Extension call activity
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Received, made, total, and missed for {rangeConfig.label.toLowerCase()}{' '}
                  (from the call records)
                </p>
              </div>
              {lastUpdated && (
                <span className="text-xs text-gray-400">
                  Updated {lastUpdated.toLocaleTimeString()}
                </span>
              )}
            </div>

            {/* The same numbers as the Today's Stats cards for the same period */}
            {summary && (
              <div className="mb-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatTile
                  size="md"
                  surface="inset"
                  label="Received"
                  dotClassName="bg-primary-500"
                  value={summary.received}
                />
                <StatTile
                  size="md"
                  surface="inset"
                  label="Made"
                  dotClassName="bg-indigo-500"
                  value={summary.made}
                />
                <StatTile size="md" surface="inset" label="Total" value={summary.total} />
                <StatTile
                  size="md"
                  surface="inset"
                  label="Missed"
                  dotClassName="bg-red-500"
                  value={summary.missed}
                  caption={`Voicemail ${summary.voicemail}`}
                />
              </div>
            )}
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-4">
              Each call counts once, however many legs it has. Missed means the caller never
              spoke to anyone. An extension's unanswered rings include calls someone else
              answered; truly missed are the ones nobody answered. One call can ring several
              extensions, so the extension rows can add up to more than the totals above.
              IVR hang-ups under 5 seconds are left out.
            </p>

            <div className="mb-5 flex flex-col sm:flex-row gap-3">
              <label className="relative flex-1">
                <span className="sr-only">Search extensions</span>
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="search"
                  value={analyticsExtensionSearch}
                  onChange={(event) => setAnalyticsExtensionSearch(event.target.value)}
                  placeholder="Search by extension number or name..."
                  className="w-full rounded-full border border-transparent bg-panel dark:bg-gray-900/60 py-2.5 pl-10 pr-14 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {analyticsExtensionSearch && (
                  <button
                    type="button"
                    onClick={() => setAnalyticsExtensionSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                    aria-label="Clear extension search"
                  >
                    Clear
                  </button>
                )}
              </label>

              <label className="relative sm:w-64">
                <span className="sr-only">Sort extension statistics</span>
                <ArrowUpDown
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <select
                  value={extensionSort}
                  onChange={(event) =>
                    setExtensionSort(event.target.value as ExtensionSortKey)
                  }
                  className="w-full appearance-none rounded-full border border-transparent bg-panel dark:bg-gray-900/60 py-2.5 pl-10 pr-8 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="total">Most total calls</option>
                  <option value="received">Most calls received</option>
                  <option value="made">Most calls made</option>
                  <option value="unansweredRings">Most unanswered rings</option>
                  <option value="trulyMissed">Most truly missed</option>
                </select>
              </label>
            </div>

            {analyticsExtensionSearch && extensionStats.length > 0 && (
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Showing {visibleExtensionStats.length} of {extensionStats.length}{' '}
                extensions
              </p>
            )}

            {extensionStats.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No extension data available.
              </p>
            ) : visibleExtensionStats.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-8 text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  No matching extension found
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Try another extension number or name.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {visibleExtensionStats.map((stat) => {
                  const receivedWidth = (stat.received / maxExtensionTotal) * 100;
                  const madeWidth = (stat.made / maxExtensionTotal) * 100;
                  const unansweredWidth = (stat.unansweredRings / maxExtensionTotal) * 100;

                  return (
                    <div key={stat.id} className="space-y-2">
                      <div className="flex flex-wrap justify-between gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <span className="font-medium">{stat.label}</span>
                        <span className="tabular-nums text-xs sm:text-sm">
                          <span>R {stat.received}</span>
                          {' · '}
                          <span>M {stat.made}</span>
                          {' · '}
                          <span className="font-semibold text-gray-900 dark:text-white">
                            Total {stat.total}
                          </span>
                          {' · '}
                          <span title="Rang here but wasn't picked up. The call may have been answered elsewhere.">
                            Unanswered rings {stat.unansweredRings}
                          </span>
                          {' · '}
                          <span className={stat.trulyMissed > 0 ? 'text-red-600 dark:text-red-400' : ''}>
                            Truly missed {stat.trulyMissed}
                          </span>
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <div className="h-2 bg-primary-100 dark:bg-primary-900/40 rounded-full">
                            <div
                              className="h-2 bg-primary-500 rounded-full"
                              style={{ width: `${Math.min(receivedWidth, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Received
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
                            <div
                              className="h-2 bg-indigo-500 rounded-full"
                              style={{ width: `${Math.min(madeWidth, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Made
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 bg-amber-100 dark:bg-amber-900/40 rounded-full">
                            <div
                              className="h-2 bg-amber-500 rounded-full"
                              style={{ width: `${Math.min(unansweredWidth, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Unanswered rings
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
      </div>
    </Card>
  );
}
